import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  INotificationSettings,
  TNotificationSettingsApiResponse,
} from "@/types/(my-space)/notification.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { NOTIFICATION_SETTINGS } from "@/constants/apiEndpoints";
import { setNotificationSettings } from "@/store/slices/user.slice";

// Mirror the fetched/updated settings into the user slice so app-wide consumers
// (e.g. the websocket notification listener deciding whether to read aloud) can
// read the latest preferences synchronously without re-querying the cache.
const pickSettingsForSlice = async (
  queryFulfilled: Promise<{ data: TNotificationSettingsApiResponse }>,
) => {
  try {
    const { data } = await queryFulfilled;
    const s = data?.data;
    if (!s) return null;
    return {
      isEmailNotificationEnabled: s.isEmailNotificationEnabled,
      isPushNotificationEnabled: s.isPushNotificationEnabled,
      isSoundNotificationEnabled: s.isSoundNotificationEnabled,
      isTextToSpeechNotificationEnabled: s.isTextToSpeechNotificationEnabled,
    };
  } catch {
    // GET/PATCH failure is surfaced by the calling component; nothing to sync.
    return null;
  }
};

export const notificationApi = createApi({
  reducerPath: "notificationApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getNotificationSettings"],
  endpoints: (builder) => ({
    getNotificationSettings: builder.query<
      TNotificationSettingsApiResponse,
      void
    >({
      query: () => ({
        url: NOTIFICATION_SETTINGS,
        method: "GET",
      }),
      providesTags: ["getNotificationSettings"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const settings = await pickSettingsForSlice(queryFulfilled);
        if (settings) dispatch(setNotificationSettings(settings));
      },
    }),
    updateNotificationSettings: builder.mutation<
      TNotificationSettingsApiResponse,
      { body: Partial<INotificationSettings> }
    >({
      query: ({ body }) => ({
        url: NOTIFICATION_SETTINGS,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getNotificationSettings"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const settings = await pickSettingsForSlice(queryFulfilled);
        if (settings) dispatch(setNotificationSettings(settings));
      },
    }),
  }),
});

export const {
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} = notificationApi;
